import React from 'react';
import { leerMarkdown } from '../utils/markdown';

/**
 * Pinta un documento en Markdown como elementos de React.
 *
 * No usa `dangerouslySetInnerHTML` a propósito. El contenido es nuestro, así
 * que inyectarlo no sería un riesgo hoy; pero esta es la pantalla donde el
 * usuario acepta obligaciones, y no conviene que la forma de mostrarla dependa
 * de que nadie escriba nunca marcado en un archivo de texto.
 */

function Piezas({ piezas }) {
    return piezas.map((pieza, i) => {
        if (pieza.tipo === 'fuerte') {
            return <strong key={i} className="font-bold text-slate-900 dark:text-white">{pieza.texto}</strong>;
        }
        if (pieza.tipo === 'codigo') {
            return (
                <code key={i} className="px-1 py-0.5 rounded bg-slate-100 dark:bg-white/10 text-[0.92em] font-mono">
                    {pieza.texto}
                </code>
            );
        }
        if (pieza.tipo === 'enlace') {
            // Los enlaces internos de los documentos apuntan a archivos .md del
            // repositorio, que no son direcciones del sitio. Se muestran como
            // texto resaltado en vez de como un enlace que lleva a ninguna parte.
            const esExterno = /^https?:\/\//.test(pieza.destino);
            if (!esExterno) {
                return <span key={i} className="font-semibold text-brand-800 dark:text-brand-300">{pieza.texto}</span>;
            }
            return (
                <a key={i} href={pieza.destino} target="_blank" rel="noreferrer"
                   className="font-semibold text-brand-800 dark:text-brand-300 underline">
                    {pieza.texto}
                </a>
            );
        }
        return <React.Fragment key={i}>{pieza.texto}</React.Fragment>;
    });
}

const TAMANOS = {
    1: 'text-2xl md:text-3xl font-black mt-0 mb-4',
    2: 'text-xl font-bold mt-8 mb-3',
    3: 'text-base font-bold mt-6 mb-2',
    4: 'text-sm font-bold mt-4 mb-2',
};

export default function Markdown({ fuente }) {
    const bloques = leerMarkdown(fuente);

    return (
        <div className="text-sm leading-relaxed text-slate-700 dark:text-slate-300">
            {bloques.map((bloque, i) => {
                if (bloque.tipo === 'titulo') {
                    const Etiqueta = `h${Math.min(bloque.nivel, 6)}`;
                    return (
                        <Etiqueta key={i} className={`${TAMANOS[bloque.nivel] || TAMANOS[4]} text-slate-900 dark:text-white`}>
                            <Piezas piezas={bloque.piezas} />
                        </Etiqueta>
                    );
                }
                if (bloque.tipo === 'lista') {
                    return (
                        <ul key={i} className="list-disc pl-5 space-y-1.5 my-3">
                            {bloque.puntos.map((punto, j) => (
                                <li key={j}><Piezas piezas={punto} /></li>
                            ))}
                        </ul>
                    );
                }
                if (bloque.tipo === 'tabla') {
                    return (
                        <div key={i} className="my-4 overflow-x-auto">
                            <table className="w-full text-xs border-collapse">
                                <thead>
                                    <tr>
                                        {bloque.encabezado.map((celda, j) => (
                                            <th key={j} className="text-left font-bold p-2 border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5">
                                                <Piezas piezas={celda} />
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {bloque.filas.map((fila, j) => (
                                        <tr key={j}>
                                            {fila.map((celda, k) => (
                                                <td key={k} className="p-2 align-top border border-slate-200 dark:border-white/10">
                                                    <Piezas piezas={celda} />
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    );
                }
                if (bloque.tipo === 'regla') {
                    return <hr key={i} className="my-6 border-slate-200 dark:border-white/10" />;
                }
                return (
                    <p key={i} className="my-3">
                        <Piezas piezas={bloque.piezas} />
                    </p>
                );
            })}
        </div>
    );
}
